import styled from "@emotion/styled";
import { IonContent, IonModal } from "@ionic/react";
import { useWindowSize } from "@react-hooks-library/core";
import React, { FC, useEffect, useRef, useState } from "react";
import { tracker } from "src/state/state";
import { TrackingData } from "src/state/Track/TrackCubit";

const SheetContent = styled.div`
  label: SheetContent;
  padding: 2rem 1.6rem calc(var(--safe-area-inset-bottom) + 3rem);
  margin-bottom: calc(100vh - var(--height-ratio, 0) * 100vh);
`;

interface BottomSheetProps {
  isOpen: boolean;
  onClose: () => unknown | Promise<unknown>;
  children?: React.ReactNode;
  trackingName?: string;
  trackingData?: TrackingData;
}

export const BottomSheet: FC<BottomSheetProps> = ({
  isOpen,
  onClose,
  children,
  trackingName,
  trackingData
}) => {
  const modal = useRef<HTMLIonModalElement>(null);
  const sheetContentRef = useRef<HTMLDivElement>(null);
  const [breakpoint, setBreakpoint] = useState(0);
  const { height } = useWindowSize();
  const checkHeightInterval = useRef<ReturnType<typeof setInterval> | null>(
    null
  );

  const checkHeight = () => {
    const sheetEl = sheetContentRef.current;
    const modalEl = modal.current;

    if (modalEl && sheetEl) {
      const requiredHeightPercentage = Math.min(
        sheetEl.clientHeight / height,
        0.75
      );

      setBreakpoint(requiredHeightPercentage);

      sheetEl.addEventListener("touchmove", (e) => {
        e.stopPropagation();
      });
      sheetEl.addEventListener("mousemove", (e) => {
        e.stopPropagation();
      });
    }
  };

  useEffect(() => {
    if (isOpen) {
      if (trackingName) {
        tracker.track(`${trackingName} Opened`, {
          data: trackingData ?? {}
        });
      }

      checkHeightInterval.current = setInterval(checkHeight, 100);
      setTimeout(() => {
        if (checkHeightInterval.current) {
          clearInterval(checkHeightInterval.current);
        }
      }, 2000);
    } else {
      if (trackingName) {
        tracker.track(`${trackingName} Closed`, {
          data: trackingData ?? {}
        });
      }

      if (checkHeightInterval.current) {
        clearInterval(checkHeightInterval.current);
      }
    }

    return () => {
      if (checkHeightInterval.current) {
        clearInterval(checkHeightInterval.current);
      }
    };
  }, [isOpen]);

  return (
    <IonModal
      ref={modal}
      isOpen={isOpen}
      onDidDismiss={onClose}
      initialBreakpoint={breakpoint}
      breakpoints={[0, breakpoint]}
      key={breakpoint}
      backdropDismiss={true}
    >
      <IonContent>
        <SheetContent
          ref={sheetContentRef}
          style={
            {
              "--height-ratio": `${breakpoint}`
            } as React.CSSProperties
          }
        >
          {children}
        </SheetContent>
      </IonContent>
    </IonModal>
  );
};
